import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { DataGrid, GridRowsProp } from "@mui/x-data-grid";
import { ReactComponent as ChatBubbleIcon } from "assets/mail-chat-bubble-square-warning.svg";
import ThemeContext from "components/app/Context";
import Typography from "components/common/Typography";
import { orderBy, uniqBy } from "lodash";
import { useContext, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { abovePropertyViewTypeProperty, messages } from "utils/CommonConstants";
import { getBrandCodesForAboveProperties, getHltInncodes } from "utils/CommonFunctions";
import { fetchAboveProperties, fetchBookmarkPropertyOrPlan, setAllProperties, setBrands } from "../AboveProperty.slice";
import BookmarkedProperties from "../BookmarkedProperties";
import FilterButtons from "../FilterButtons";
import { headers } from "./TableHeadersConfig";

const PropertyView = () => {
  const dispatch = useAppDispatch();
  const { isMobileScreen } = useContext(ThemeContext);
  const inncodeOptions = getHltInncodes();
  const { aboveProperties, abovePropertyLoader, filters, allProperties } = useAppSelector(
    (state) => state.aboveProperty
  );
  const innCodes = localStorage.getItem("hltInnCodes");
  const { brandCodes, engagements, inncode, searchText } = filters.propertyView.property;
  const isFiltersEmpty = Object.values(filters?.propertyView?.property).every((x: any) => !x.length);

  useEffect(() => {
    dispatch(
      fetchAboveProperties({
        searchText,
        brandCodes: getBrandCodesForAboveProperties(brandCodes, allProperties),
        engagements,
        filteredInnCodes: inncode
      })
    ).then((res: any) => {
      if (isFiltersEmpty) {
        const brandOptions = orderBy(uniqBy(res?.payload?.properties, "brandName"), "brandName");
        dispatch(setBrands(brandOptions));
        dispatch(setAllProperties(res?.payload?.properties));
        dispatch(fetchBookmarkPropertyOrPlan({ viewType: abovePropertyViewTypeProperty }));
      }
    });
  }, [brandCodes, engagements, inncode, searchText, innCodes]);

  const rows: GridRowsProp = [...aboveProperties.map((a: any) => ({ ...a, id: a?.innCode }))];

  const noRows = () => {
    return (
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ margin: "16px", backgroundColor: tokens.Background.General.Secondary, py: "8px" }}
      >
        <ChatBubbleIcon />
        <Typography variant="body1" color={tokens.Text.General.Tertiary}>
          {messages.noAbovePropertiesData}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      {isMobileScreen && <BookmarkedProperties />}
      <Box
        sx={{
          backgroundColor: tokens.Background.General.Primary,
          borderRadius: "12px",
          overflow: "hidden",
          mt: isMobileScreen ? "32px" : 0,
          mb: "32px"
        }}
      >
        <FilterButtons
          isBrandsVisible
          isSearchBarVisible
          isEngagementVisible
          filterType="property"
          parentView="propertyView"
          isInncodeVisible
          optionsList={{ inncode: inncodeOptions }}
          searchPredefinedValue={searchText}
          searchBarPlaceHolder="Search by property"
        />
        <Divider />
        {!abovePropertyLoader && (
          <DataGrid
            autoHeight
            rows={rows}
            columns={headers}
            disableColumnMenu
            disableColumnFilter
            density="comfortable"
            disableColumnSelector
            getRowHeight={() => "auto"}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: noRows
            }}
            sx={{
              "&.MuiDataGrid-root": {
                border: "none",
                color: tokens.Text.General.PrimaryBody,
                backgroundColor: tokens.Background.General.Primary,

                ".MuiDataGrid-cell": {
                  padding: "16px"
                },

                ".MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none !important"
                },

                ".MuiDataGrid-overlay": {
                  backgroundColor: tokens.Background.General.Primary
                }
              }
            }}
          />
        )}
      </Box>
    </>
  );
};

export default PropertyView;
